// Pure port of src/utils/form/conditionEvaluator.js's evaluation logic
// (evaluateCondition / evaluateFieldVisibility / evaluateDisabledCondition
// only - the file's other exports, getOperatorOptions/operatorNeedsValue/
// getValidationPatterns/getPatternDescription, are admin-UI helpers for
// NewFieldModal, not needed by rendering or validation).
//
// Deliberately duplicated rather than imported: the old file lives outside
// the `_` tree and is still the live implementation for the other ~14
// dynamic modals not yet on this engine (see docs/external-dependency-audit.md).
// Keep this in sync with conditionEvaluator.js if that file's condition
// semantics change.

const getFieldTypeByName = (fieldName, allFields = []) => {
  for (const section of allFields) {
    const field = section.fields?.find((f) => f.name === fieldName);
    if (field) {
      return field.type;
    }
  }
  return null;
};

export const evaluateCondition = (condition, formData, allFields = []) => {
  if (!condition || typeof condition !== "object") {
    return true;
  }

  if (condition.type === "complex" && condition.conditions) {
    const result = condition.conditions.every((subCondition) =>
      evaluateCondition(subCondition, formData, allFields)
    );
    return condition.negate ? !result : result;
  }

  if (condition.type === "arrayIncludes" && condition.field && condition.values) {
    const fieldValue = formData[condition.field];
    const result =
      Array.isArray(condition.values) && condition.values.includes(fieldValue);
    return condition.negate ? !result : result;
  }

  if (condition.type === "arrayLength" && condition.field) {
    const fieldValue = formData[condition.field];
    if (!Array.isArray(fieldValue)) {
      const result = false;
      return condition.negate ? !result : result;
    }

    let result;
    switch (condition.operator) {
      case "greaterThan":
        result = fieldValue.length > parseInt(condition.value);
        break;
      case "lessThan":
        result = fieldValue.length < parseInt(condition.value);
        break;
      case "equals":
        result = fieldValue.length === parseInt(condition.value);
        break;
      case "greaterThanOrEqual":
        result = fieldValue.length >= parseInt(condition.value);
        break;
      case "lessThanOrEqual":
        result = fieldValue.length <= parseInt(condition.value);
        break;
      default:
        result = fieldValue.length > 0;
    }
    return condition.negate ? !result : result;
  }

  if (!condition.field || !condition.operator) {
    const result = true;
    return condition.negate ? !result : result;
  }

  const getNestedValue = (obj, path) =>
    path.split(".").reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : undefined;
    }, obj);

  const fieldValue = getNestedValue(formData, condition.field);
  const fieldType = getFieldTypeByName(condition.field, allFields);

  const getFieldValueForComparison = (value, type) => {
    if (type === "singleSelect" && typeof value === "object" && value !== null) {
      return value.value || value;
    }
    return value;
  };

  const actualFieldValue = getFieldValueForComparison(fieldValue, fieldType);

  let result;
  switch (condition.operator) {
    case "equals":
      result = actualFieldValue === condition.value;
      break;
    case "notEquals":
      result = actualFieldValue !== condition.value;
      break;
    case "contains":
      result = Array.isArray(actualFieldValue)
        ? actualFieldValue.includes(condition.value)
        : Boolean(actualFieldValue && actualFieldValue.includes(condition.value));
      break;
    case "notContains":
      result = Array.isArray(actualFieldValue)
        ? !actualFieldValue.includes(condition.value)
        : !actualFieldValue || !actualFieldValue.includes(condition.value);
      break;
    case "isTrue":
      result = Boolean(actualFieldValue);
      break;
    case "isFalse":
      result = !actualFieldValue;
      break;
    case "isEmpty":
      result =
        !actualFieldValue ||
        actualFieldValue === "" ||
        (Array.isArray(actualFieldValue) && actualFieldValue.length === 0);
      break;
    case "isNotEmpty":
      result =
        actualFieldValue &&
        actualFieldValue !== "" &&
        (!Array.isArray(actualFieldValue) || actualFieldValue.length > 0);
      break;
    case "greaterThan":
      result = Number(actualFieldValue) > Number(condition.value);
      break;
    case "lessThan":
      result = Number(actualFieldValue) < Number(condition.value);
      break;
    case "greaterThanOrEqual":
      result = Number(actualFieldValue) >= Number(condition.value);
      break;
    case "lessThanOrEqual":
      result = Number(actualFieldValue) <= Number(condition.value);
      break;
    default:
      result = true;
  }

  return condition.negate ? !result : result;
};

export const evaluateFieldVisibility = (field, formData, allFields = []) => {
  if (field.showCondition) {
    if (!evaluateCondition(field.showCondition, formData, allFields)) return false;
  }

  if (field.additionalShowCondition) {
    if (!evaluateCondition(field.additionalShowCondition, formData, allFields)) {
      return false;
    }
  }

  // Legacy escape hatch some older schemas still carry - see the equivalent
  // check in the old src/utils/form/renderInput.js switch.
  if (typeof field.showIf === "function" && !field.showIf(formData)) {
    return false;
  }

  return true;
};

// Whether a whole section should show - same rule DynamicForm/index.jsx
// renders by (this used to be a private copy that lived only there).
// buildResolver.js needs the identical logic so validation agrees with
// rendering about what's actually visible - a field inside a hidden
// section (e.g. CompanyFormModal's shipping-address section, hidden via
// showSectionCondition when "same as billing" is checked) shouldn't be
// required just because the user can't see it to fill in.
export const evaluateSectionVisibility = (section, formData, allFields = []) => {
  if (section.showSectionCondition && typeof section.showSectionCondition === "object") {
    return evaluateCondition(section.showSectionCondition, formData, allFields);
  }
  if (typeof section.showSection === "function") {
    return section.showSection(formData);
  }
  if (section.showSection === false) {
    return false;
  }

  // No explicit section-level rule - fall back to "does this section have
  // at least one field that would actually render". A section whose every
  // field is individually hidden via field.showCondition (e.g. per-origin
  // sections like "MongoDB URL"/"Google Sheet URL" that only apply to one
  // originSource value) has nothing to show, so the section itself
  // shouldn't render its header either. Sections with no fields at all
  // (e.g. a pure informational header) still show, matching prior
  // behavior for every existing consumer.
  if (section.showSection === undefined && Array.isArray(section.fields) && section.fields.length > 0) {
    const hasVisibleField = section.fields.some((field) => evaluateFieldVisibility(field, formData, allFields));
    if (!hasVisibleField) return false;
  }

  return true;
};

// Whether a field is required *right now* - same three-way precedence
// (function > requiredCondition > plain boolean) buildResolver.js's
// validateField uses to decide whether to raise a "required" error, plus
// its "a pre-filled default means don't nag" carve-out. Shared here so a
// field's required-asterisk (FormField.jsx, renderInput.jsx) always agrees
// with whether it will actually block submit - a field whose requirement
// is conditional (e.g. CompanyFormModal's companyShortName, required only
// when isIndividual is false) previously showed no asterisk at all because
// callers read the raw `field.required` boolean instead of evaluating it.
export const evaluateRequiredCondition = (field, formData, allFields = []) => {
  let isRequired;
  if (typeof field.required === "function") {
    isRequired = field.required(formData);
  } else if (field.requiredCondition && typeof field.requiredCondition === "object") {
    isRequired = evaluateCondition(field.requiredCondition, formData, allFields);
  } else {
    isRequired = Boolean(field.required);
  }

  if (
    isRequired &&
    field.defaultValue !== undefined &&
    field.defaultValue !== null &&
    field.defaultValue !== ""
  ) {
    isRequired = false;
  }

  return isRequired;
};

export const evaluateDisabledCondition = (
  field,
  formData,
  allFields = [],
  mode = "create"
) => {
  if (field.disabled === true) return true;
  if (field.disabled === false) return false;

  if (field.disabledMode) {
    switch (field.disabledMode) {
      case "edit":
        return mode === "edit";
      case "create":
        return mode === "create";
      case "view":
        return mode === "view";
      default:
        break;
    }
  }

  if (field.disabledCondition) {
    return evaluateCondition(field.disabledCondition, formData, allFields);
  }

  if (typeof field.disabled === "function") {
    return field.disabled(formData, mode);
  }

  return false;
};
